/**
 * GSTIN & branches form defaults — mirrors the company form's
 * read/write seam. Only two server-mutable fields live on this page in
 * V1.0:
 *   - `gstin`                  (locked once set; first-time entry allowed)
 *   - `type`                   (Registration type — Regular/Composition/SEZ/Casual)
 *   - `defaultPlaceOfSupply`   (PoS mode — migration 0044)
 *
 * Everything else surfaced on the page (state of registration, registered
 * date, branches, e-Invoice, e-Way bill, RCM toggles) is either derived
 * read-only or a V1.x placeholder.
 */
import type { z } from "zod";
import type { companies } from "@flint/api-contract";

type CompanyRow = z.infer<typeof companies.getOutput>;
type CompanyUpdate = z.infer<typeof companies.updateInput>;

export interface GstinFormValues {
  gstin: string;
  type: string;
  /** `from_customer | always_sender_state | ask_per_invoice` */
  defaultPlaceOfSupply: string;
}

export const EMPTY_GSTIN_FORM: GstinFormValues = {
  gstin: "",
  type: "Regular",
  defaultPlaceOfSupply: "from_customer",
};

export function formFromCompany(c: CompanyRow): GstinFormValues {
  return {
    gstin: c.gstin ?? "",
    type: c.type,
    defaultPlaceOfSupply: c.defaultPlaceOfSupply || "from_customer",
  };
}

/**
 * Build the wire patch from a diff. GSTIN is locked-once-set — only
 * included when the baseline is empty (the server enforces it as the
 * floor; we fail fast in the SPA).
 */
export function valuesToUpdatePayload(
  v: GstinFormValues,
  base: GstinFormValues,
): CompanyUpdate | null {
  const patch: CompanyUpdate = {};
  if (v.type && v.type !== base.type) {
    patch.type = v.type as CompanyUpdate["type"];
  }
  if (v.defaultPlaceOfSupply && v.defaultPlaceOfSupply !== base.defaultPlaceOfSupply) {
    patch.defaultPlaceOfSupply = v.defaultPlaceOfSupply as CompanyUpdate["defaultPlaceOfSupply"];
  }
  // GSTIN — first-time entry only. Server returns 409 if baseline is set.
  if (!base.gstin.trim() && v.gstin.trim() !== base.gstin.trim()) {
    patch.gstin = v.gstin.trim().toUpperCase();
  }
  return Object.keys(patch).length === 0 ? null : patch;
}

export function isDirtyVs(v: GstinFormValues, base: GstinFormValues): boolean {
  return (
    v.gstin.trim() !== base.gstin.trim() ||
    v.type !== base.type ||
    v.defaultPlaceOfSupply !== base.defaultPlaceOfSupply
  );
}
